import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { TalkFormData, Talk } from '../types';

const TalkFormPage: React.FC = () => {
  const { talkId } = useParams<{ talkId: string }>();
  const navigate = useNavigate();
  const isEditMode = !!talkId;

  const [formData, setFormData] = useState<TalkFormData>({
    title: '',
    summary: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditMode);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEditMode && talkId) {
      fetchTalk();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talkId, isEditMode]);

  const fetchTalk = async () => {
    try {
      setFetchLoading(true);
      const response = await apiClient.get(`/v1/talks/${talkId}`);
      const talk: Talk = response.data.data;
      setFormData({
        title: talk.title,
        summary: talk.summary,
        description: talk.description
      });
    } catch (err: any) {
      console.error('Error fetching talk:', err);
      setError(err.response?.data?.error?.message || 'Failed to load talk');
    } finally {
      setFetchLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }

    if (!formData.summary.trim()) {
      errors.summary = 'Summary is required';
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (isEditMode) {
        await apiClient.put(`/v1/talks/${talkId}`, formData);
        navigate(`/talks/${talkId}`);
      } else {
        const response = await apiClient.post('/v1/talks', formData);
        const newTalkId = response.data.data.talk_id;
        navigate(`/talks/${newTalkId}`);
      }
    } catch (err: any) {
      console.error('Error saving talk:', err);
      setError(err.response?.data?.error?.message || 'Failed to save talk');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  if (fetchLoading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="mb-3">
            <Link to={isEditMode ? `/talks/${talkId}` : '/talks'} className="btn btn-link text-decoration-none">
              <i className="fas fa-arrow-left me-2"></i>
              Back
            </Link>
          </div>

          <div className="card">
            <div className="card-header bg-white">
              <h3 className="mb-0">
                <i className="fas fa-microphone me-2"></i>
                {isEditMode ? 'Edit Talk' : 'Create New Talk'}
              </h3>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="title" className="form-label">
                    Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${validationErrors.title ? 'is-invalid' : ''}`}
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter talk title"
                    disabled={loading}
                  />
                  {validationErrors.title && (
                    <div className="invalid-feedback">{validationErrors.title}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="summary" className="form-label">
                    Summary <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${validationErrors.summary ? 'is-invalid' : ''}`}
                    id="summary"
                    name="summary"
                    rows={3}
                    value={formData.summary}
                    onChange={handleChange}
                    placeholder="Brief description of your talk"
                    disabled={loading}
                  />
                  {validationErrors.summary && (
                    <div className="invalid-feedback">{validationErrors.summary}</div>
                  )}
                  <div className="form-text">
                    A brief overview that will be displayed in listings
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="description" className="form-label">
                    Full Description <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${validationErrors.description ? 'is-invalid' : ''}`}
                    id="description"
                    name="description"
                    rows={8}
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Detailed description of your talk content, topics covered, and learning outcomes"
                    disabled={loading}
                  />
                  {validationErrors.description && (
                    <div className="invalid-feedback">{validationErrors.description}</div>
                  )}
                  <div className="form-text">
                    Provide detailed information about what attendees will learn
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        {isEditMode ? 'Update Talk' : 'Create Talk'}
                      </>
                    )}
                  </button>
                  <Link
                    to={isEditMode ? `/talks/${talkId}` : '/talks'}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalkFormPage;
